import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Loader2,
  Mail,
  Phone,
  Shield,
  UserCheck,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import verificationService, {
  VerificationProgress,
} from "../../services/verification";
import UKWorkPermitModal from "../UKWorkPermitModal";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../ui/use-toast";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

const otpSchema = z.object({
  otpCode: z
    .string()
    .min(6, "OTP code must be 6 digits")
    .max(6, "OTP code must be 6 digits"),
});

const phoneSchema = z.object({
  phoneNumber: z.string().min(10, "Please enter a valid phone number"),
});

type EmailFormData = z.infer<typeof emailSchema>;
type OtpFormData = z.infer<typeof otpSchema>;
type PhoneFormData = z.infer<typeof phoneSchema>;

interface HorizontalStepperProps {
  steps: Array<{
    number: number;
    title: string;
    icon: React.ReactNode;
    status: "completed" | "current" | "pending";
  }>;
  onStepClick?: (stepNumber: number) => void;
}

const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  steps,
  onStepClick,
}) => {
  return (
    <div className="w-full py-6">
      <div className="flex items-center justify-between relative">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className="flex flex-col items-center flex-1 relative z-10">
              <div
                onClick={() => {
                  // Only allow clicking on completed steps
                  if (step.status === "completed" && onStepClick) {
                    onStepClick(step.number);
                  }
                }}
                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                  step.status === "completed"
                    ? "bg-green-500 border-green-500 text-white cursor-pointer hover:bg-green-600"
                    : step.status === "current"
                    ? "bg-purple-500 border-purple-500 text-white"
                    : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 cursor-not-allowed"
                }`}
              >
                {step.status === "completed" ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  step.icon
                )}
              </div>
              <p
                className={`mt-2 text-sm font-medium ${
                  step.status === "current"
                    ? "text-white"
                    : step.status === "completed"
                    ? "text-gray-300 cursor-pointer hover:text-white"
                    : "text-gray-400"
                }`}
                onClick={() => {
                  // Only allow clicking on completed steps
                  if (step.status === "completed" && onStepClick) {
                    onStepClick(step.number);
                  }
                }}
              >
                {step.title}
              </p>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 ${
                  step.status === "completed"
                    ? "bg-green-500"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const VerificationFlow: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get channel and step from URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const channel = urlParams.get("channel") || "email";
  const stepFromQuery = urlParams.get("step");

  const [progress, setProgress] = useState<VerificationProgress | null>(null);
  const [loading, setLoading] = useState(true);
  // Initialize currentStep from query string if available, otherwise default to 1
  // If step=4 is in query and we have Stripe return params, prioritize step 4
  const urlParamsInitial = new URLSearchParams(window.location.search);
  const hasStripeReturn = !!(
    urlParamsInitial.get("return_url") || urlParamsInitial.get("session_id")
  );
  // Always start from step 2 when opening stepper (Step 1 removed, except Stripe return)
  const [currentStep, setCurrentStep] = useState(2);
  // isInitialLoadRef: true only on the very first load when there's no step in URL and no Stripe return
  // Once user interacts or page refreshes with step in URL, this becomes false
  const isInitialLoadRef = useRef(!stepFromQuery && !hasStripeReturn);
  // Track if this is truly the first time opening (not a refresh with existing data)
  // Only true if: no step in URL AND no existing selection in backend
  // If step is in URL or backend has data, this is a refresh/resume, not first time
  const isFirstTimeOpening = useRef(!stepFromQuery && !hasStripeReturn);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showUKWorkPermitModal, setShowUKWorkPermitModal] = useState(false);
  // Always start with no selection (null) - user must select an option
  const [ukNativeType, setUkNativeType] = useState<string | null>(null);

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
  });

  const otpForm = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
  });

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
  });

  // Update URL query string when currentStep changes
  // BUT: Don't update if we're returning from Stripe (to preserve step=4)
  useEffect(() => {
    if (token) {
      const urlParams = new URLSearchParams(window.location.search);
      const isReturningFromStripe = !!(
        urlParams.get("return_url") || urlParams.get("session_id")
      );

      // If returning from Stripe and step is already 4, don't override it
      if (
        isReturningFromStripe &&
        urlParams.get("step") === "4" &&
        currentStep === 4
      ) {
        console.log("🔄 Preserving step=4 from Stripe return");
        return; // Don't update URL
      }

      urlParams.set("step", currentStep.toString());
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [currentStep, token]);

  useEffect(() => {
    if (token) {
      console.log(
        "🔍 Loading progress for token:",
        token,
        "currentStep:",
        currentStep
      );
      loadProgress(token, channel);
    }
  }, [token, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark that initial load is done after first loadProgress call
  useEffect(() => {
    if (progress && isInitialLoadRef.current) {
      // After first progress load, mark that initial load is done
      // This prevents going back to step 1 on subsequent loads
      isInitialLoadRef.current = false;
    }
  }, [progress]);

  // Check for Stripe return and refresh progress
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeReturn = urlParams.get("return_url");
    const stripeSessionId = urlParams.get("session_id");

    console.log("🔍 Checking Stripe return:", {
      stripeReturn,
      stripeSessionId,
      token,
    });

    if ((stripeReturn || stripeSessionId) && token) {
      // User returned from Stripe, refresh progress immediately
      console.log("✅ User returned from Stripe, refreshing progress...");

      // Load progress immediately to check status and update step
      loadProgress(token, channel);
    }
  }, [token, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProgress = async (token: string, channelParam?: string) => {
    try {
      console.log(
        "🔄 Loading progress for token:",
        token,
        "channel:",
        channelParam
      );
      setLoading(true);

      // Check if we're returning from Stripe BEFORE loading progress
      // Read step from URL fresh each time (not from closure)
      const urlParams = new URLSearchParams(window.location.search);
      const stripeReturn = urlParams.get("return_url");
      const stripeSessionId = urlParams.get("session_id");
      const currentStepFromQuery = urlParams.get("step"); // Read fresh from URL
      const isReturningFromStripe = !!(stripeReturn || stripeSessionId);

      console.log("🔍 loadProgress - URL params:", {
        stripeReturn,
        stripeSessionId,
        currentStepFromQuery,
        isReturningFromStripe,
      });

      const data = await verificationService.getProgress(token, channelParam);
      console.log("📊 Progress data received:", data);

      setProgress(data);
      setEmployeeId(data.employeeId);

      // Check if all steps are completed
      const isUKNativeCheck = !!data.ukNativeType;
      const allCompleted = isUKNativeCheck
        ? data.steps.email.status === "pass" &&
          data.steps.phone.status === "pass" &&
          data.steps.id.status === "pass"
        : data.steps.email.status === "pass" &&
          data.steps.phone.status === "pass" &&
          data.steps.id.status === "pass" &&
          (data.steps.rtw.status === "pass" ||
            data.steps.rtw.status === "skipped");

      // If all steps are completed, don't set currentStep (will show success message)
      // Otherwise, determine the appropriate step based on verification status
      if (!allCompleted) {
        // PRIORITY 1: If first time opening (no step in URL, no Stripe return, AND no backend selection)
        // Only if: no step in URL AND no Stripe return AND ukNativeType is undefined in backend
        // This means user is truly opening the form for the first time
        const isTrulyFirstTime =
          !currentStepFromQuery &&
          !isReturningFromStripe &&
          data.ukNativeType === undefined;

        if (isTrulyFirstTime) {
          console.log(
            "🎯 First time opening form, starting from Step 2 (Email)",
            {
              isFirstTimeOpening: isFirstTimeOpening.current,
              isReturningFromStripe,
              currentStepFromQuery,
              dataUkNativeType: data.ukNativeType,
              emailStatus: data.steps.email.status,
              phoneStatus: data.steps.phone.status,
            }
          );
          // Start from Step 2 (Email) - Step 1 removed
          setCurrentStep(2);
          setUkNativeType(null); // Reset to show placeholder - user must select
          setLoading(false); // Stop loading immediately
          // IMPORTANT: Don't set isFirstTimeOpening to false here - keep it true until user makes a selection
          // This prevents loadProgress from setting ukNativeType from backend on subsequent calls
          // Return early to prevent any further processing
          return; // Early return - don't process any step calculation or set ukNativeType
        }

        // PRIORITY 2: If returning from Stripe, preserve step 4
        if (isReturningFromStripe || currentStepFromQuery === "4") {
          console.log("🔄 Returning from Stripe, preserving step 4", {
            isReturningFromStripe,
            currentStepFromQuery,
            stripeReturn,
            stripeSessionId,
          });
          setCurrentStep(4);
          // Continue to process Stripe return logic below
        } else {
          // User refreshed or has interacted before, resume from appropriate step
          // After initial load (e.g., on refresh), resume from appropriate step based on progress
          // Step 1 removed - start from Step 2 (Email)
          let nextStep = 2; // Default to Step 2 (Email Verification)

          // Step 2: Email Verification
          // For email channel: email is auto-verified, skip to Step 3
          // For SMS channel: check if email is verified
          if (nextStep >= 2) {
            if (channel === "email" && data.steps.email.status === "pass") {
              // Email channel: email already verified, skip to phone
              nextStep = 3;
            } else if (
              channel === "sms" &&
              data.steps.email.status === "pass"
            ) {
              // SMS channel: email verified, move to phone
              nextStep = 3;
            } else {
              // Email not verified yet, stay on Step 2
              nextStep = 2;
            }
          }

          // Step 3: Phone Verification
          if (nextStep >= 3 && data.steps.phone.status === "pass") {
            nextStep = 4; // Phone verified, move to ID
          } else if (nextStep > 3 && data.steps.phone.status !== "pass") {
            nextStep = 3; // Need to verify phone
          }

          // Step 4: ID Verification
          if (nextStep >= 4 && data.steps.id.status === "pass") {
            if (isUKNativeCheck) {
              // UK Native: All steps done (no RTW), will show success
              // Don't change step
            } else {
              nextStep = 5; // Move to RTW
            }
          } else if (nextStep > 4 && data.steps.id.status !== "pass") {
            nextStep = 4; // Need to verify ID
          }

          // Step 5: RTW Check (only if not UK Native)
          // If RTW is done, all steps are completed (handled above)

          console.log("📍 Setting current step to:", nextStep, {
            isUKNative: isUKNativeCheck,
            emailStatus: data.steps.email.status,
            phoneStatus: data.steps.phone.status,
            idStatus: data.steps.id.status,
            rtwStatus: data.steps.rtw.status,
            channel,
            ukNativeType: data.ukNativeType,
          });

          // Only set step if not returning from Stripe (we already set it to 4 above)
          if (!isReturningFromStripe) {
            setCurrentStep(nextStep);
          }
        }
      }

      // Set UK Native status from backend
      // IMPORTANT: Only set if NOT first time opening (first time opening already returned above)
      // If isFirstTimeOpening is true, we already returned above, so this code won't run
      // This ensures that on first load, ukNativeType stays null (no selection)
      if (!isFirstTimeOpening.current) {
        // If ukNativeType is null, it means user selected "No" option (value "none" in UI)
        // If ukNativeType is undefined, it means user hasn't selected yet (show placeholder)
        // If ukNativeType has a value, it means user selected a UK status option
        // For UI, we use "none" to represent null (No option selected)
        // IMPORTANT: null in backend means "No" was selected, so we set it to "none" in UI
        if (data.ukNativeType === null) {
          setUkNativeType("none"); // "No" option was selected
        } else if (data.ukNativeType === undefined) {
          setUkNativeType(null); // Not selected yet
        } else {
          setUkNativeType(data.ukNativeType); // UK status option selected
        }
      }
      // If isFirstTimeOpening.current is true, ukNativeType stays null (no selection)

      // Check if ID verification was just completed (returned from Stripe)
      // Note: stripeReturn and stripeSessionId are already checked above

      console.log("🔍 Checking ID verification status:", {
        stripeReturn,
        stripeSessionId,
        idStatus: data.steps.id.status,
        allCompleted,
      });

      // If user returned from Stripe and ID verification passed
      if (
        (stripeReturn || stripeSessionId) &&
        data.steps.id.status === "pass"
      ) {
        console.log("🎉 ID verification passed!");

        try {
          // Remove query params from URL to clean it up
          const cleanUrl = window.location.pathname;
          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, "", cleanUrl);
          }
        } catch (error) {
          console.warn("⚠️ Could not clean URL:", error);
          // Continue anyway - this is not critical
        }

        // Check if all steps are now completed
        if (allCompleted) {
          // All steps completed - don't set currentStep, will show success message
          // Auto-complete
          setTimeout(async () => {
            if (data.employeeId) {
              try {
                await verificationService.complete({
                  employeeId: data.employeeId,
                });
                // Reload progress again to show success message
                await loadProgress(token, channel);
              } catch (error) {
                console.error("Auto-complete error:", error);
              }
            }
          }, 1000);
        } else {
          // Not all steps completed yet, move to next step
          if (isUKNativeCheck) {
            // UK Native: Step 4 (ID) is the last step
            setCurrentStep(4);
          } else {
            // Non-UK Native: Move to step 5 (RTW check)
            setCurrentStep(5);
          }
        }

        toast({
          title: "🎉 Identity Verification Successful!",
          description:
            "Your identity has been successfully verified by Stripe.",
        });
      } else if (
        (stripeReturn || stripeSessionId) &&
        data.steps.id.status === "fail"
      ) {
        console.log("❌ ID verification failed!");

        try {
          // Remove query params from URL
          const cleanUrl = window.location.pathname;
          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, "", cleanUrl);
          }
        } catch (error) {
          console.warn("⚠️ Could not clean URL:", error);
          // Continue anyway
        }

        toast({
          title: "❌ Identity Verification Failed",
          description:
            "Your identity verification was not successful. Please try again.",
          variant: "destructive",
        });
      } else if (stripeReturn || stripeSessionId) {
        // User returned from Stripe but status not yet updated, just clean URL
        try {
          const cleanUrl = window.location.pathname;
          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, "", cleanUrl);
          }
        } catch (error) {
          console.warn("⚠️ Could not clean URL:", error);
          // Continue anyway
        }
      }

      // Handle email channel - set email form value if email is verified
      if (data.steps.email.status === "pass") {
        emailForm.setValue("email", data.employee?.email || "");
      }

      // Handle SMS channel - if no email exists, show email input first
      if (
        channel === "sms" &&
        !data.employee?.email &&
        data.currentStep === 1
      ) {
        setShowEmailInput(true);
      }

      // Handle Step 3 (Phone Verification)
      // For Email channel: if phone exists, will show OTP form directly (no confirmation needed)
      // For SMS channel: phone confirmation is handled in render (always show phone and ask)
    } catch (error: any) {
      console.error("❌ Error loading progress:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (data: EmailFormData) => {
    if (!token || !employeeId) return;

    setIsSubmitting(true);
    try {
      // For SMS channel: update email and send OTP (don't verify directly)
      if (channel === "sms") {
        // Update email first
        await verificationService.updateEmail({
          employeeId,
          email: data.email,
        });

        toast({
          title: "Email Updated",
          description: "Your email has been updated successfully.",
        });

        setShowEmailInput(false);
        await loadProgress(token!);

        // Automatically request OTP to email
        setTimeout(() => {
          handleRequestOtp();
        }, 500);
      } else {
        // For email channel: verify email directly (email is already verified via link)
        await verificationService.verifyEmail({
          token,
          email: data.email,
        });

        toast({
          title: "Email Verified",
          description: "Your email has been successfully verified.",
        });

        setShowEmailInput(false);
        await loadProgress(token!);

        // Move to next step
        handleNext();
      }
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneSubmit = async (data: PhoneFormData) => {
    if (!employeeId || !token) return;

    setIsSubmitting(true);
    try {
      // Update phone number in backend
      await verificationService.updatePhoneNumber({
        employeeId,
        phoneNumber: data.phoneNumber,
      });

      toast({
        title: "Phone Number Updated",
        description: "Your phone number has been updated successfully.",
      });

      setShowPhoneInput(false);
      phoneForm.reset();

      // Reload progress to get updated phone number
      await loadProgress(token, channel);

      // For email channel: automatically send OTP after entering phone number
      // For SMS channel: show confirmation first
      if (channel === "email") {
        // Automatically request OTP to the new phone number
        setTimeout(() => {
          handleRequestOtp();
        }, 500);
      }
      // For SMS channel, just close the input form (user will see confirmation)
    } catch (error: any) {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestOtp = async (phoneNumber?: string) => {
    if (!employeeId) return;

    setOtpLoading(true);
    try {
      // Determine OTP channel based on current step and invitation channel
      // Step 2: Email OTP (for SMS channel users who need to verify email)
      // Step 3: Phone OTP (for both channels - SMS users verify phone, Email users verify phone)
      let otpChannel: "email" | "sms";
      if (currentStep === 2 && channel === "sms") {
        // Step 2: SMS channel users need to verify email via OTP
        otpChannel = "email";
      } else if (currentStep === 3) {
        // Step 3: Both channels need to verify phone via OTP
        otpChannel = "sms";
      } else {
        // Fallback: For email channel in step 2, send to SMS
        otpChannel = channel === "sms" ? "email" : "sms";
      }

      // Set otpSent to true after successfully sending OTP
      setOtpSent(true);

      const response = await verificationService.requestOtp({
        employeeId,
        channel: otpChannel,
        phoneNumber: phoneNumber,
      });

      setOtpSent(true);
      const destination =
        otpChannel === "email" ? "email address" : "mobile number";
      toast({
        title: "OTP Sent",
        description: `OTP code sent to your ${destination}. ${
          response.otpCode ? `Code: ${response.otpCode}` : ""
        }`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to Send OTP",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpSubmit = async (data: OtpFormData) => {
    if (!employeeId) return;

    setIsSubmitting(true);
    try {
      // Determine OTP type based on current step
      // Step 2: Email OTP (for SMS channel users)
      // Step 3: Phone OTP (for both channels)
      let otpType: "email" | "phone";
      if (currentStep === 2 && channel === "sms") {
        // Step 2: SMS channel users verify email via OTP
        otpType = "email";
      } else if (currentStep === 3) {
        // Step 3: Both channels verify phone via OTP
        otpType = "phone";
      } else {
        // Fallback
        otpType = channel === "sms" ? "email" : "phone";
      }

      const result = await verificationService.verifyOtp({
        employeeId,
        code: data.otpCode,
        type: otpType,
      });

      if (result.verified) {
        const verificationType = otpType === "email" ? "Email" : "Phone";
        toast({
          title: `${verificationType} Verified`,
          description: `Your ${verificationType.toLowerCase()} has been successfully verified.`,
        });
        setOtpSent(false);
        otpForm.reset();
        await loadProgress(token!);
        // Automatically move to next step
        setTimeout(() => {
          handleNext();
        }, 500);
      } else {
        toast({
          title: "Verification Failed",
          description: result.message || "Invalid OTP code",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIdVerification = async () => {
    if (!employeeId) return;

    setIsSubmitting(true);
    try {
      const response = await verificationService.createIdSession({
        employeeId,
      });

      toast({
        title: "Redirecting to Stripe Identity",
        description:
          "You will be redirected to complete your identity verification.",
      });

      // Redirect to Stripe Identity
      window.location.href = response.url;
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handleUKNativeChange = async (value: string) => {
    if (!token) return;

    try {
      setIsSubmitting(true);
      // Handle the selection:
      // - Empty string "" means placeholder selected -> don't save, return early
      // - "none" means "No - I will complete the UK Work Permit verification step" -> send null to backend
      // - Any other value means a UK status option -> send that value to backend
      if (value === "") {
        // Placeholder selected, don't do anything
        setIsSubmitting(false);
        return;
      }
      const ukNativeTypeToSet = value === "none" ? null : value;
      await verificationService.setUKNative(token, ukNativeTypeToSet);
      // For UI, keep the value as-is ("none" for "No" option, or the actual value)
      setUkNativeType(value);

      // Mark that user has started interacting (no longer initial load)
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }

      // Mark that first time opening is done - user has made a selection
      if (isFirstTimeOpening.current) {
        isFirstTimeOpening.current = false;
      }

      // If "none" is selected, open UK permit modal
      if (value === "none") {
        setShowUKWorkPermitModal(true);
      }

      // Reload progress to get updated maxSteps
      await loadProgress(token, channel);

      // Don't show toast - just update silently so user can continue
    } catch (error: any) {
      console.error("Error setting UK Native:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update UK status",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    // Mark that user has started navigating (no longer initial load)
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
    if (currentStep < maxSteps) {
      let nextStep = currentStep + 1;
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 2) {
      // Step 1 removed, minimum is Step 2
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRtwCheck = () => {
    if (!employeeId) {
      toast({
        title: "Error",
        description: "Employee ID not found",
        variant: "destructive",
      });
      return;
    }
    setShowUKWorkPermitModal(true);
  };

  const handleUKWorkPermitSuccess = async (result: any) => {
    if (!employeeId) return;

    setIsSubmitting(true);
    try {
      // Call backend API to save RTW check result
      await verificationService.checkRtw({
        employeeId,
      });

      // Reload progress to update step status
      await loadProgress(token!);

      // Don't set currentStep - loadProgress will handle it based on completion status
      // Auto-complete verification
      setTimeout(async () => {
        if (employeeId) {
          try {
            await verificationService.complete({ employeeId });
            // Reload progress again to show success message
            await loadProgress(token!);
          } catch (error) {
            console.error("Auto-complete error:", error);
          }
        }
      }, 1000);

      toast({
        title: "✅ Right to Work Verified",
        description:
          result.status?.details || "Your right to work has been confirmed.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save verification result",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate max steps based on UK Native status
  // Step 1: Email Verification (was Step 2)
  // Step 2: Phone Verification (was Step 3)
  // Step 3: ID Verification (was Step 4)
  // Step 4: RTW (only if not UK Native, was Step 5)
  // UK Status Selection is now in the last step
  // Note: ukNativeType can be:
  // - null/undefined: not selected yet
  // - "none": user selected "No" option (needs RTW check, so NOT UK Native)
  // - "uk_ireland", "eea_settlement", "brp_visa": user selected UK status (is UK Native, skips RTW)
  const isUKNative =
    ukNativeType && ukNativeType !== "none" && ukNativeType !== "";
  const maxSteps = isUKNative ? 3 : 4; // 3 steps if UK Native, 4 if not (including "none" selection)

  // Check if all steps are completed
  // Don't depend on currentStep, just check if all required steps are passed
  const allStepsCompleted = progress
    ? isUKNative
      ? progress.steps.email.status === "pass" &&
        progress.steps.phone.status === "pass" &&
        progress.steps.id.status === "pass"
      : progress.steps.email.status === "pass" &&
        progress.steps.phone.status === "pass" &&
        progress.steps.id.status === "pass" &&
        (progress.steps.rtw.status === "pass" ||
          progress.steps.rtw.status === "skipped")
    : false;

  // Helper to get step status for horizontal stepper
  // Note: Step numbers in stepper are now 1-indexed for display (Email=1, Phone=2, ID=3, RTW=4)
  // But actual currentStep values are 2, 3, 4, 5 (Step 1 removed)
  const getStepperStepStatus = (
    stepNum: number
  ): "completed" | "current" | "pending" => {
    // Map stepper step numbers to actual step numbers:
    // Stepper 1 (Email) = actual Step 2
    // Stepper 2 (Phone) = actual Step 3
    // Stepper 3 (ID) = actual Step 4
    // Stepper 4 (RTW) = actual Step 5
    const actualStepNum = stepNum + 1; // Add 1 because we removed Step 1

    // Step 1 (Email, actual Step 2)
    if (stepNum === 1) {
      const emailStep = 2;
      if (currentStep === emailStep) return "current";
      if (currentStep > emailStep) return "completed";
      return "pending";
    }

    // Step 2 (Phone, actual Step 3)
    if (stepNum === 2) {
      const phoneStep = 3;
      if (currentStep === phoneStep) return "current";
      if (currentStep > phoneStep) return "completed";
      return "pending";
    }

    // Step 3 (ID, actual Step 4)
    if (stepNum === 3) {
      const idStep = 4;
      if (currentStep === idStep) return "current";
      if (currentStep > idStep) return "completed";
      return "pending";
    }

    // Step 4 (RTW, actual Step 5)
    if (stepNum === 4) {
      const rtwStep = 5;
      if (currentStep === rtwStep) return "current";
      if (currentStep > rtwStep) return "completed";
      return "pending";
    }

    // Default
    if (actualStepNum < currentStep) return "completed";
    if (actualStepNum === currentStep) return "current";
    return "pending";
  };

  const handleStepClick = (stepNumber: number) => {
    // Mark that user has started navigating (no longer initial load)
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
    // Convert stepper step number to actual step number
    // Stepper Step 1 = actual Step 2 (Email)
    // Stepper Step 2 = actual Step 3 (Phone)
    // Stepper Step 3 = actual Step 4 (ID)
    // Stepper Step 4 = actual Step 5 (RTW)
    const actualStepNumber = stepNumber + 1;
    // Only allow navigation to completed steps or current step
    const stepStatus = getStepperStepStatus(stepNumber);
    if (stepStatus === "completed" || stepStatus === "current") {
      setCurrentStep(actualStepNumber);
    }
  };

  // Auto-complete when all steps are completed
  useEffect(() => {
    if (allStepsCompleted && employeeId && progress) {
      // Check if employee is already verified to avoid duplicate calls
      if (progress.status !== "verified") {
        console.log("🎉 All steps completed, calling complete method...");
        const completeTimer = setTimeout(async () => {
          try {
            await verificationService.complete({ employeeId });
            console.log("✅ Complete method called successfully");
            // Reload progress to update status
            if (token) {
              await loadProgress(token, channel);
            }
          } catch (error) {
            console.error("❌ Auto-complete error:", error);
          }
        }, 500);

        return () => clearTimeout(completeTimer);
      }
    }
  }, [allStepsCompleted, employeeId, progress?.status, token, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build steps array for horizontal stepper (Step 1 removed - UK Status moved to last step)
  // Note: stepper step numbers are 1, 2, 3, 4 but actual step numbers are 2, 3, 4, 5
  const stepperSteps = [
    {
      number: 1,
      title: "Email",
      icon: <Mail className="w-6 h-6" />,
      status: getStepperStepStatus(1), // Stepper step 1 = actual step 2
    },
    {
      number: 2,
      title: "Phone",
      icon: <Phone className="w-6 h-6" />,
      status: getStepperStepStatus(2), // Stepper step 2 = actual step 3
    },
    {
      number: 3,
      title: "Identity",
      icon: <UserCheck className="w-6 h-6" />,
      status: getStepperStepStatus(3), // Stepper step 3 = actual step 4
    },
  ];

  // Add RTW step only if not UK Native
  if (!isUKNative) {
    stepperSteps.push({
      number: 4,
      title: "RTW Check",
      icon: <Shield className="w-6 h-6" />,
      status: getStepperStepStatus(4), // Stepper step 4 = actual step 5
    });
  }

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!token || !employeeId || !progress) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 mx-auto text-red-600 mb-4" />
              <h2 className="text-2xl font-bold mb-2">
                Invalid Verification Link
              </h2>
              <p className="text-muted-foreground mb-6">
                The verification link is invalid or has expired.
              </p>
              <Button onClick={() => navigate("/")}>Return to Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Identity Verification</h1>
        <p className="text-muted-foreground">
          Complete your identity verification in {maxSteps} simple steps
        </p>
      </div>

      {/* Show Stepper only if not all steps completed */}
      {!allStepsCompleted && (
        <Card className="bg-gray-900 dark:bg-gray-950">
          <CardContent className="p-6">
            {/* Horizontal Stepper */}
            <HorizontalStepper
              steps={stepperSteps}
              onStepClick={handleStepClick}
            />
          </CardContent>
        </Card>
      )}

      {/* Show Success Message if all steps completed */}
      {allStepsCompleted ? (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="text-center space-y-4 py-8">
              <CheckCircle className="h-20 w-20 mx-auto text-green-600" />
              <h2 className="text-2xl font-semibold">All Steps Completed!</h2>
              <p className="text-muted-foreground text-lg">
                Your verification is complete. Thank you for completing the
                identity verification process.
              </p>
              {ukNativeType && (
                <p className="text-sm text-muted-foreground italic mt-4">
                  Note: UK Work Permit verification was skipped based on your
                  selected status.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>
              {currentStep === 2
                ? "Email Verification"
                : currentStep === 3
                ? channel === "sms"
                  ? "Email OTP Verification"
                  : "Phone Verification"
                : currentStep === 4
                ? "Identity Verification"
                : currentStep === 5
                ? "Right to Work Check"
                : ""}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1 removed - UK Status Selection moved to last step */}

            {/* Step 2: Email Verification */}
            {currentStep === 2 && (
              <div className="space-y-4">
                {/* Show OTP form if OTP was sent (for SMS channel after email is entered) */}
                {otpSent && channel === "sms" && progress?.employee?.email ? (
                  <form
                    onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="otpCode">Enter Verification Code</Label>
                      <p className="text-sm text-muted-foreground">
                        Please enter the verification code sent to{" "}
                        {progress.employee.email}
                      </p>
                      <Input
                        id="otpCode"
                        type="text"
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        {...otpForm.register("otpCode")}
                      />
                      {otpForm.formState.errors.otpCode && (
                        <p className="text-sm text-destructive">
                          {otpForm.formState.errors.otpCode.message}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setOtpSent(false);
                          otpForm.reset();
                        }}
                      >
                        Previous
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleRequestOtp()}
                          disabled={otpLoading}
                        >
                          {otpLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            "Resend Code"
                          )}
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Verifying...
                            </>
                          ) : (
                            "Confirm"
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <>
                    {/* Show email input form if email is not verified or not exists */}
                    {(channel !== "email" ||
                      showEmailInput ||
                      !progress?.employee?.email) && (
                      <form
                        onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
                        className="space-y-4"
                      >
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="Enter your email address"
                            {...emailForm.register("email")}
                          />
                          {emailForm.formState.errors.email && (
                            <p className="text-sm text-destructive">
                              {emailForm.formState.errors.email.message}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-between">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handlePrevious}
                          >
                            Previous
                          </Button>
                          <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {channel === "sms" && !progress?.employee?.email
                                  ? "Updating..."
                                  : "Sending Code..."}
                              </>
                            ) : (
                              <>
                                {channel === "sms" && !progress?.employee?.email
                                  ? "Update Email"
                                  : "Verify Email"}
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    )}
                    {/* Show verified email message if email is already verified (for email channel) */}
                    {channel === "email" &&
                      progress?.employee?.email &&
                      !showEmailInput && (
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            Email verified: {progress.employee.email}
                          </p>
                          <div className="flex justify-between">
                            <Button variant="outline" onClick={handlePrevious}>
                              Previous
                            </Button>
                            <Button onClick={handleNext}>
                              Next
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Phone OTP Verification */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-4">
                  {/* Show phone input form if user wants to change phone number */}
                  {showPhoneInput ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="phoneNumber">
                          Enter New Phone Number
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Current phone number:{" "}
                          {progress?.employee?.phoneE164 || "Not set"}
                        </p>
                        <Input
                          id="phoneNumber"
                          type="tel"
                          placeholder="+44 1234567"
                          {...phoneForm.register("phoneNumber")}
                        />
                        {phoneForm.formState.errors.phoneNumber && (
                          <p className="text-sm text-destructive">
                            {phoneForm.formState.errors.phoneNumber.message}
                          </p>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowPhoneInput(false);
                            phoneForm.reset();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={phoneForm.handleSubmit(handlePhoneSubmit)}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            "Update Phone Number"
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : channel === "email" ? (
                    /* Email channel: Show input form if no phone, or OTP form if phone exists */
                    <>
                      {!progress?.employee?.phoneE164 ? (
                        /* No phone number: show input form */
                        <form
                          onSubmit={phoneForm.handleSubmit(handlePhoneSubmit)}
                          className="space-y-4"
                        >
                          <div className="space-y-2">
                            <Label htmlFor="phoneNumber">
                              Enter Your Phone Number
                            </Label>
                            <Input
                              id="phoneNumber"
                              type="tel"
                              placeholder="+44 1234567"
                              {...phoneForm.register("phoneNumber")}
                            />
                            {phoneForm.formState.errors.phoneNumber && (
                              <p className="text-sm text-destructive">
                                {phoneForm.formState.errors.phoneNumber.message}
                              </p>
                            )}
                          </div>
                          <div className="flex justify-between">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handlePrevious}
                            >
                              Previous
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Sending Code...
                                </>
                              ) : (
                                <>
                                  Send Code
                                  <ArrowRight className="ml-2 h-4 w-4" />
                                </>
                              )}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        /* Phone number exists: show OTP form with option to change phone */
                        <form
                          onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
                          className="space-y-4"
                        >
                          <div className="space-y-2">
                            <Label htmlFor="otpCode">Enter OTP Code</Label>
                            <p className="text-sm text-muted-foreground">
                              We've sent a verification code to{" "}
                              {progress?.employee?.phoneE164 ||
                                "your mobile number"}
                            </p>
                            <Input
                              id="otpCode"
                              type="text"
                              placeholder="Enter 6-digit code"
                              maxLength={6}
                              {...otpForm.register("otpCode")}
                            />
                            {otpForm.formState.errors.otpCode && (
                              <p className="text-sm text-destructive">
                                {otpForm.formState.errors.otpCode.message}
                              </p>
                            )}
                          </div>
                          <div className="flex justify-between items-center">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowPhoneInput(true);
                                setOtpSent(false);
                                otpForm.reset();
                              }}
                            >
                              Change Phone Number
                            </Button>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleRequestOtp()}
                                disabled={otpLoading}
                              >
                                {otpLoading ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  "Resend Code"
                                )}
                              </Button>
                              <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Verifying...
                                  </>
                                ) : (
                                  <>
                                    Confirm
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </form>
                      )}
                    </>
                  ) : channel === "sms" && progress?.employee?.phoneE164 ? (
                    /* SMS channel: Phone number exists, show confirmation or OTP form */
                    <>
                      {!otpSent ? (
                        <div className="space-y-4">
                          {/* Show current phone number */}
                          <div className="p-4 bg-muted rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium">
                                  Phone Number
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {progress?.employee?.phoneE164 || "Not set"}
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowPhoneInput(true)}
                              >
                                Change
                              </Button>
                            </div>
                          </div>

                          <p className="text-sm text-muted-foreground">
                            Do you want to send the verification code to this
                            number?
                          </p>
                          <div className="flex justify-between">
                            <Button variant="outline" onClick={handlePrevious}>
                              Previous
                            </Button>
                            <Button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleRequestOtp();
                              }}
                              disabled={otpLoading}
                            >
                              {otpLoading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Sending Code...
                                </>
                              ) : (
                                "Send Code"
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <form
                          onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
                          className="space-y-4"
                        >
                          <div className="space-y-2">
                            <Label htmlFor="otpCode">Enter OTP Code</Label>
                            <p className="text-sm text-muted-foreground">
                              We've sent a verification code to{" "}
                              {progress?.employee?.phoneE164 ||
                                "your mobile number"}
                            </p>
                            <Input
                              id="otpCode"
                              type="text"
                              placeholder="Enter 6-digit code"
                              maxLength={6}
                              {...otpForm.register("otpCode")}
                            />
                            {otpForm.formState.errors.otpCode && (
                              <p className="text-sm text-destructive">
                                {otpForm.formState.errors.otpCode.message}
                              </p>
                            )}
                          </div>
                          <div className="flex justify-between items-center">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowPhoneInput(true);
                                setOtpSent(false);
                                otpForm.reset();
                              }}
                            >
                              Change Phone Number
                            </Button>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleRequestOtp()}
                                disabled={otpLoading}
                              >
                                {otpLoading ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  "Resend Code"
                                )}
                              </Button>
                              <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Verifying...
                                  </>
                                ) : (
                                  <>
                                    Confirm
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </form>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {/* Step 4: ID Verification */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  You will be redirected to Stripe Identity to verify your
                  identity document.
                </p>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={handlePrevious}>
                    Previous
                  </Button>
                  <Button
                    onClick={handleIdVerification}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Starting Verification...
                      </>
                    ) : (
                      "Start Identity Verification"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: RTW Check - Only show if NOT UK Native (including "none" selection) */}
            {/* Now includes UK Status Selection */}
            {currentStep === 5 && !isUKNative && (
              <div className="space-y-4">
                {/* UK Status Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    What is your UK right-to-work status?
                  </Label>
                  <select
                    value={
                      ukNativeType === null || ukNativeType === undefined
                        ? ""
                        : ukNativeType
                    }
                    onChange={(e) => {
                      handleUKNativeChange(e.target.value);
                    }}
                    disabled={isSubmitting}
                    required
                    className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                      ukNativeType === null ||
                      ukNativeType === undefined ||
                      ukNativeType === ""
                        ? "border-destructive"
                        : "border-input"
                    }`}
                  >
                    <option value="">-- Please select an option --</option>
                    <option value="none">
                      No - I will complete the UK Work Permit verification step
                    </option>
                    <option value="uk_ireland">
                      Yes - UK/Ireland national
                    </option>
                    <option value="eea_settlement">
                      Yes - EEA national with eVisas or EU Settlement Scheme
                    </option>
                    <option value="brp_visa">
                      Yes - Others with physical BRP/visa vignette
                    </option>
                  </select>
                  {!ukNativeType && (
                    <p className="text-sm text-destructive">
                      Please select an option to continue.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    If you select a citizen or visa option, HRKYC™️ will guide
                    you through the correct right-to-work route automatically.
                    You can change this selection before you submit.
                  </p>
                </div>

                {/* RTW Check button - only show if "No" is selected */}
                {ukNativeType === "none" && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      We will check your right to work status with UK
                      authorities.
                    </p>
                    <div className="flex justify-between">
                      <Button variant="outline" onClick={handlePrevious}>
                        Previous
                      </Button>
                      <Button onClick={handleRtwCheck} disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Checking RTW...
                          </>
                        ) : (
                          "Check Right to Work"
                        )}
                      </Button>
                    </div>
                  </>
                )}

                {/* Complete button - show if UK status is selected (not "none") */}
                {ukNativeType &&
                  ukNativeType !== "none" &&
                  ukNativeType !== "" && (
                    <div className="flex justify-between">
                      <Button variant="outline" onClick={handlePrevious}>
                        Previous
                      </Button>
                      <Button
                        onClick={async () => {
                          // Complete verification since UK status is selected
                          if (employeeId) {
                            try {
                              setIsSubmitting(true);
                              await verificationService.complete({
                                employeeId,
                              });
                              await loadProgress(token!);
                              toast({
                                title: "✅ Verification Complete",
                                description:
                                  "Your verification has been completed successfully.",
                              });
                            } catch (error: any) {
                              toast({
                                title: "Error",
                                description:
                                  error.message ||
                                  "Failed to complete verification",
                                variant: "destructive",
                              });
                            } finally {
                              setIsSubmitting(false);
                            }
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Completing...
                          </>
                        ) : (
                          "Complete Verification"
                        )}
                      </Button>
                    </div>
                  )}
              </div>
            )}

            {/* Step 4: ID Verification - Add UK Status Selection if UK Native */}
            {currentStep === 4 && isUKNative && (
              <div className="space-y-4">
                {progress?.steps.id.status !== "pass" ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      You will be redirected to Stripe Identity to verify your
                      identity document.
                    </p>
                    <div className="flex justify-between">
                      <Button variant="outline" onClick={handlePrevious}>
                        Previous
                      </Button>
                      <Button
                        onClick={handleIdVerification}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Starting Verification...
                          </>
                        ) : (
                          "Start Identity Verification"
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  /* Show UK Status Selection after ID is verified */
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">
                      What is your UK right-to-work status?
                    </Label>
                    <select
                      value={
                        ukNativeType === null || ukNativeType === undefined
                          ? ""
                          : ukNativeType
                      }
                      onChange={(e) => {
                        handleUKNativeChange(e.target.value);
                      }}
                      disabled={isSubmitting}
                      required
                      className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        ukNativeType === null ||
                        ukNativeType === undefined ||
                        ukNativeType === ""
                          ? "border-destructive"
                          : "border-input"
                      }`}
                    >
                      <option value="">-- Please select an option --</option>
                      <option value="none">
                        I will provide a UK share code / work permit step
                      </option>
                      <option value="uk_ireland">UK or Irish citizen</option>
                      <option value="eea_settlement">
                        EEA citizen with EU Settlement Scheme / eVisa
                      </option>
                      <option value="brp_visa">
                        Non-EEA with BRP or visa vignette
                      </option>
                    </select>
                    {!ukNativeType && (
                      <p className="text-sm text-destructive">
                        Please select an option to continue.
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      If you select a citizen or visa option, HRKYC™️ will guide
                      you through the correct right-to-work route automatically.
                      You can change this selection before you submit.
                    </p>
                    {ukNativeType &&
                      ukNativeType !== "none" &&
                      ukNativeType !== "" && (
                        <div className="flex justify-between">
                          <Button variant="outline" onClick={handlePrevious}>
                            Previous
                          </Button>
                          <Button
                            onClick={async () => {
                              // Complete verification since UK status is selected
                              if (employeeId) {
                                try {
                                  setIsSubmitting(true);
                                  await verificationService.complete({
                                    employeeId,
                                  });
                                  await loadProgress(token!);
                                  toast({
                                    title: "✅ Verification Complete",
                                    description:
                                      "Your verification has been completed successfully.",
                                  });
                                } catch (error: any) {
                                  toast({
                                    title: "Error",
                                    description:
                                      error.message ||
                                      "Failed to complete verification",
                                    variant: "destructive",
                                  });
                                } finally {
                                  setIsSubmitting(false);
                                }
                              }
                            }}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Completing...
                              </>
                            ) : (
                              "Complete Verification"
                            )}
                          </Button>
                        </div>
                      )}
                    {ukNativeType === "none" && (
                      <div className="flex justify-between">
                        <Button variant="outline" onClick={handlePrevious}>
                          Previous
                        </Button>
                        <Button
                          onClick={handleRtwCheck}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Checking RTW...
                            </>
                          ) : (
                            "Check Right to Work"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* UK Work Permit Modal */}
      {employeeId && (
        <UKWorkPermitModal
          open={showUKWorkPermitModal}
          onOpenChange={setShowUKWorkPermitModal}
          onSuccess={handleUKWorkPermitSuccess}
          employeeId={employeeId}
        />
      )}
    </div>
  );
};

export default VerificationFlow;
